import type {
    IOneVerifyEraUploadCompleteRequestData,
    IOneVerifyEraUploadPresignRequestData,
    IOneVerifyIntegrationSupportRequestData,
} from "@common-shared/types/requestData/oneVerifyRequestData.types"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types"
import type {
    IOneVerifyEraUploadCompleteResponse,
    IOneVerifyEraUploadPresignResponse,
    IOneVerifyIntegrationSupportResponse,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { API_ROUTES } from "../config/routes.config"
import { HttpHelper } from "../helpers/http.helper"
import { UtilHelper } from "../helpers/util.helper"

export class IntegrationsService {
    /**
     * Submit a support request for an integration.
     */
    static async submitIntegrationSupportRequest(
        practice: IPractice,
        data: IOneVerifyIntegrationSupportRequestData,
    ): Promise<IOneVerifyIntegrationSupportResponse | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.INTEGRATIONS_SUPPORT.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            data,
            "POST",
        )

        if (
            result &&
            !UtilHelper.isEmptyObject(result) &&
            !result.errorData &&
            result.submitted
        ) {
            return result
        }

        return undefined
    }

    /**
     * Create a presigned URL for an ERA upload.
     */
    static async createEraUploadPresignedUrl(
        practice: IPractice,
        data: IOneVerifyEraUploadPresignRequestData,
    ): Promise<IOneVerifyEraUploadPresignResponse | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.INTEGRATIONS_ERA_UPLOAD_PRESIGN.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            data,
            "POST",
        )

        if (result?.uploadUrl && result?.s3Key) {
            return result
        }

        return undefined
    }

    /**
     * Complete an ERA upload.
     */
    static async completeEraUpload(
        practice: IPractice,
        data: IOneVerifyEraUploadCompleteRequestData,
    ): Promise<IOneVerifyEraUploadCompleteResponse | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.INTEGRATIONS_ERA_UPLOAD_COMPLETE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            data,
            "POST",
        )

        if (result?.s3Key) {
            return result
        }

        return undefined
    }

    /**
     * Upload a file to a presigned URL.
     */
    static async uploadFileToPresignedUrl(
        uploadUrl: string,
        file: File,
        contentType: string,
        onProgress?: (percent: number) => void,
    ): Promise<boolean> {
        return await new Promise(resolve => {
            const xhr = new XMLHttpRequest()

            xhr.open("PUT", uploadUrl)
            xhr.setRequestHeader("Content-Type", contentType)

            if (onProgress) {
                xhr.upload.onprogress = event => {
                    if (!event.lengthComputable) {
                        return
                    }

                    onProgress(Math.round((event.loaded / event.total) * 100))
                }
            }

            xhr.onload = () => {
                resolve(xhr.status >= 200 && xhr.status < 300)
            }

            xhr.onerror = () => {
                resolve(false)
            }

            xhr.send(file)
        })
    }
}
